import { useEffect, useRef } from 'react';
import { AssetList, AssetRail, useAssetsHidden } from '../components/AssetList';
import { BottomTabs } from '../components/BottomTabs';
import { ChartPanel } from '../components/ChartPanel';
import { InstrumentHeader } from '../components/InstrumentHeader';
import { OrderPanel } from '../components/OrderPanel';
import { ChartSettingsButton } from '../components/ChartSettings';
import { InstantButton, InstantPanel } from '../components/InstantTrade';
import { Splitter, useSplit } from '../components/Splitter';
import { cx } from '../components/ui';
import { PriceFeedBanner, TradingStatusBanner } from '../components/TradeNotices';
import { useIsDesktop } from '../lib/media';
import { Link } from '../lib/router';
import { useInstrument, useMarketLoaded } from '../stores/market';
import { MobileTrade } from './trade/MobileTrade';

export function TradePage({ symbol }: { symbol: string }) {
  const inst = useInstrument(symbol);
  const loaded = useMarketLoaded();
  const desktop = useIsDesktop();
  // Desktop: the positions panel's height under the chart, dragged or collapsed by the trader.
  const column = useRef<HTMLDivElement>(null);
  const chart = useRef<HTMLDivElement>(null);
  const split = useSplit(column, chart, desktop);
  // Desktop: the trader can hide the asset list to give the chart more room.
  const [assetsHidden, setAssetsHidden] = useAssetsHidden();

  useEffect(() => {
    document.title = `${symbol} · Trade`;
  }, [symbol]);

  if (loaded && !inst) {
    return (
      <div className="grid flex-1 place-items-center p-8 text-center">
        <div>
          <p className="text-muted">Unknown instrument “{symbol}”.</p>
          <Link to="/markets" className="text-accent-fg">
            Browse markets
          </Link>
        </div>
      </div>
    );
  }

  // Phones: instrument screen with pinned Buy / Sell and a full-screen order ticket.
  if (!desktop) return <MobileTrade symbol={symbol} />;

  return (
    <div className="flex min-h-0 flex-1">
      <div className={cx('shrink-0 border-r border-line', !assetsHidden && 'w-[280px]')}>
        {assetsHidden ? <AssetRail onShow={() => setAssetsHidden(false)} /> : <AssetList current={symbol} onHide={() => setAssetsHidden(true)} />}
      </div>
      <div ref={column} className="flex min-w-0 flex-1 flex-col">
        <TradingStatusBanner />
        <PriceFeedBanner symbol={symbol} />
        <InstrumentHeader symbol={symbol} />
        <div ref={chart} className="flex min-h-0 flex-1 flex-col">
          <ChartPanel symbol={symbol} />
        </div>
        <Splitter h={split.h} max={split.max} collapsed={split.collapsed} onChange={split.setH} onCollapse={split.setCollapsed} />
        <div className="shrink-0" style={split.collapsed ? undefined : { height: split.h }}>
          <BottomTabs
            collapsed={split.collapsed}
            onCollapse={split.setCollapsed}
            actions={
              <>
                <InstantButton />
                <ChartSettingsButton />
              </>
            }
          />
        </div>
      </div>
      {/* The order panel runs the full height of the right side, so its submit button is never cut off. */}
      <div className="min-h-0 w-[330px] shrink-0 overflow-y-auto border-l border-line">
        <OrderPanel symbol={symbol} />
      </div>
      <InstantPanel symbol={symbol} />
    </div>
  );
}
